import { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";

// ── Count-up hook ──
function useCountUp(target, duration = 2000, step = 5) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const increment = target / (duration / step);
          let current = 0;
          const timer = setInterval(() => {
            current += increment;
            if (current >= target) {
              setCount(target);
              clearInterval(timer);
            } else {
              setCount(Math.floor(current));
            }
          }, step);
        }
      },
      { threshold: 0.3 },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration, step]);

  return { count, ref };
}

// ── Typewriter hook ──
const PHRASES = [
  "Manage your shop with confidence",
  "Track sales and profits in real time",
  "Control your inventory effortlessly",
  "Make smarter business decisions",
];

function useTypewriter(
  phrases,
  typingSpeed = 80,
  deletingSpeed = 40,
  pause = 1800,
) {
  const [text, setText] = useState("");
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const current = phrases[phraseIndex];
    let timeout;
    if (!isDeleting && text === current) {
      timeout = setTimeout(() => setIsDeleting(true), pause);
    } else if (isDeleting && text === "") {
      setIsDeleting(false);
      setPhraseIndex((i) => (i + 1) % phrases.length);
    } else {
      timeout = setTimeout(
        () => {
          setText(
            isDeleting
              ? current.slice(0, text.length - 1)
              : current.slice(0, text.length + 1),
          );
        },
        isDeleting ? deletingSpeed : typingSpeed,
      );
    }
    return () => clearTimeout(timeout);
  }, [
    text,
    isDeleting,
    phraseIndex,
    phrases,
    typingSpeed,
    deletingSpeed,
    pause,
  ]);

  return text;
}

// ── HeroSection Component ──
export function HeroSection() {
  const shops = useCountUp(500, 2000, 5);
  const accuracy = useCountUp(98, 2000, 5);
  const typedText = useTypewriter(PHRASES);

  return (
    <section id="home" className="relative overflow-hidden bg-white">
      {/* Desktop: hakuna padding kubwa left/right — content inaanza mwanzo */}
      <div className="w-full px-6 sm:px-8 lg:px-16 py-10 lg:py-28 flex flex-col lg:flex-row items-start gap-6 lg:gap-16">
        {/* ── Text Content ── */}
        <div className="flex-1 flex flex-col items-start text-left w-full lg:pl-0">
          <div className="max-w-screen-2xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center"></div>
          {/* Badge */}
          <span className="bg-white text-blue-800 text-sm font-semibold px-4 py-2 rounded-full border-2 border-blue-800">
            SMART SHOP MANAGEMENT
          </span>

          {/* Heading */}
          <h1
            className="font-extrabold  py-6 leading-tight text-gray-900 m-0 min-h-[5rem] lg:min-h-[7rem]"
            style={{ fontSize: "clamp(1.7rem, 6vw, 3.75rem)" }}
          >
            <span className="text-blue-800">Shop</span>{" "}
            <span className="text-orange-600">Kiganjani</span>
            <span className="block">
              {typedText}
              <span
                className="inline-block w-[3px] bg-blue-600 ml-px align-middle"
                style={{
                  height: "1em",
                  animation: "blink 0.8s step-end infinite",
                }}
              />
            </span>
          </h1>

          {/* Sub-text */}
          <p className="mt-4 lg:mt-6 text-lg lg:text-xl leading-relaxed text-gray-900 max-w-full lg:max-w-xl">
            Stop guessing your profits. Track every sale, monitor your inventory
            in real-time, and separate your capital from earnings all in one
            powerful platform built for shop owners.
          </p>

          {/* CTA buttons */}
          <div className="mt-5 lg:mt-8 flex flex-wrap items-center gap-3">
            <a href="/login">
              <button
                className="inline-flex items-center gap-2 px-4 py-2.5 lg:px-6 lg:py-3 text-sm lg:text-base font-bold text-white rounded-xl border-none cursor-pointer active:scale-95 transition-all duration-150"
                style={{
                  background: "linear-gradient(to right,#2563eb,#1d4ed8)",
                  boxShadow: "0 4px 14px rgba(37,99,235,0.35)",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              >
                Get Started Free
                <FontAwesomeIcon icon={faArrowRight} className="w-3.5 h-3.5" />
              </button>
            </a>
            <a href="#services">
              <button className="px-4 py-2.5 lg:px-6 lg:py-3 text-sm lg:text-base font-bold text-gray-800 bg-transparent border border-gray-300 rounded-xl cursor-pointer transition-all hover:border-blue-500 hover:bg-blue-50">
                Explore Features
              </button>
            </a>
          </div>

          {/* Stats */}
          <div
            ref={shops.ref}
            className="mt-6 py-6 lg:mt-12 flex flex-nowrap items-center gap-4 lg:gap-8 w-full"
          >
            <div className="flex flex-col items-start">
              <span className="text-xl lg:text-5xl font-extrabold text-gray-900 tabular-nums">
                {shops.count}
                <span className="text-blue-600">+</span>
              </span>
              <span className="text-[0.65rem] lg:text-sm text-gray-900 whitespace-nowrap">
                Active Shops
              </span>
            </div>

            <div className="w-px h-8 bg-gray-400 shrink-0" />

            <div ref={accuracy.ref} className="flex flex-col items-start">
              <span className="text-xl lg:text-5xl font-extrabold text-gray-900 tabular-nums">
                {accuracy.count}
                <span className="text-blue-600">%</span>
              </span>
              <span className="text-[0.65rem] lg:text-sm text-gray-900 whitespace-nowrap">
                Accuracy Rate
              </span>
            </div>

            <div className="w-px h-8 bg-gray-400 shrink-0" />

            <div className="flex flex-col items-start">
              <span className="text-xl lg:text-5xl font-extrabold text-gray-900">
                24/7
              </span>
              <span className="text-[0.65rem] lg:text-sm text-gray-900 whitespace-nowrap">
                Real-time Data
              </span>
            </div>
          </div>
        </div>

        {/* ── Dashboard image ── */}
        <div className="flex-1 w-full">
          <div className="relative rounded-2xl overflow-hidden h-96 lg:h-[420px]">
            <img
              src="/assets/images/Revenue.png"
              alt="Zanzibar beach"
              className="w-full h-full object-cover"
            />
            {/* Floating badge */}
            <div className="absolute bottom-4 left-4 rounded-lg border border-gray-200/50 bg-white/90 px-4 py-2.5 shadow-lg backdrop-blur-sm">
              <p className="m-0 text-xs font-medium text-gray-500">
                Live Sales Overview
              </p>
              <p className="m-0 text-sm font-bold text-gray-900">
                Track every transaction
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>{`@keyframes blink{0%,100%{opacity:1}50%{opacity:0}}`}</style>
    </section>
  );
}
